import { useCallback } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { Box, Stack, Typography, useTheme } from "@mui/material";

import {
  PriorClaimsForm,
  PriorClaimsFormInput,
} from "@covertree/new-business-forms";
import { SubmitButton } from "@covertree/ui";
import { PriorClaimSource } from "@covertree/new-business-graphql";
import {
  selectPriorClaimsQuestions,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { PriorClaimItem } from "@covertree/new-business-forms/src/components/PriorClaims";
import { ClaimsListHeader } from "@covertree/new-business-past-claims";

interface ClaimsListFormProps {
  priorClaimsCount: number;
  loading: boolean;
  isD2C?: boolean;
}

function ClaimsListForm({
  priorClaimsCount,
  loading,
  isD2C,
}: ClaimsListFormProps) {
  const theme = useTheme();
  const questions = useNewBusinessSelector(selectPriorClaimsQuestions);

  const {
    control,
    formState: { isValid },
  } = useFormContext<PriorClaimsForm>();

  const { append, remove, fields } = useFieldArray({
    control,
    name: "claims",
  });

  const addEmptyClaim = useCallback(() => {
    const emptyClaim: Partial<PriorClaimsFormInput> = {
      category: undefined,
      amount: undefined,
      date: "",
      catastrophe: false,
      source: PriorClaimSource.UserDeclared,
      insured: undefined,
    };

    append(emptyClaim as PriorClaimsFormInput);
  }, [append]);

  return (
    <Stack spacing={0} pt={priorClaimsCount > 0 && isD2C ? 4 : 0}>
      {!priorClaimsCount && (
        <img
          src="/images/icons/checkmark.webp"
          alt="We've found no past claims"
          style={{ maxWidth: theme.spacing(22) }}
        />
      )}

      <ClaimsListHeader
        numberOfClaims={priorClaimsCount}
        addEmptyClaim={addEmptyClaim}
        disabled={loading}
      />
      <Box>
        {fields.map((claim, index) => (
          <Box pb={3} key={`claim-item-${claim.id}`}>
            <PriorClaimItem
              key={`claim-item-${claim.id}`}
              questions={Object.values(questions)}
              claim={claim as Partial<PriorClaimsFormInput>}
              index={index}
              onDelete={() => remove(index)}
              control={control}
              disabled={loading}
            />
          </Box>
        ))}
      </Box>
      {isD2C && (
        <Typography color={theme.colors.grey2}>
          If you had any claims in the last 5 years, you must add them now.
        </Typography>
      )}
      <SubmitButton
        loading={loading}
        disabled={loading || !isValid}
        width={200}
        data-testid="continue">
        Continue
      </SubmitButton>
    </Stack>
  );
}

export { ClaimsListForm };
